import { UAParser } from "ua-parser-js";

import type { ApplyClickResponseDto, JobItemDto } from "@artha/api-contracts";

import { apiFetch } from "@/lib/apiClient";

type ApplyClickPortalContext = {
  portalId?: string;
  domainName?: string;
  clientName?: string;
  userId?: string;
  userEmail?: string;
};

type ApplyClickApiResponse = {
  success?: boolean;
  message?: string;
  data?: ApplyClickResponseDto;
};

const SESSION_STORAGE_KEY = "applyClickSessionId";

const normalizeDeviceType = (value?: string) => {
  if (value === "mobile" || value === "tablet") return value;
  return "desktop";
};

const parseUtmParams = (search: string) => {
  const params = new URLSearchParams(search);
  return {
    utm_source: params.get("utm_source") || "",
    utm_medium: params.get("utm_medium") || "",
    utm_campaign: params.get("utm_campaign") || "",
    utm_term: params.get("utm_term") || "",
    utm_content: params.get("utm_content") || "",
  };
};

const isValidUrl = (value?: string) => {
  if (!value) return false;
  try {
    new URL(value);
    return true;
  } catch {
    return false;
  }
};

const getOrCreateSessionId = () => {
  if (typeof window === "undefined") return "unknown";
  try {
    const existing = window.sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (existing) return existing;
    const nextId =
      typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    window.sessionStorage.setItem(SESSION_STORAGE_KEY, nextId);
    return nextId;
  } catch {
    return "unknown";
  }
};

const isLinkedInInAppBrowser = () => {
  if (typeof navigator === "undefined") return false;
  return /LinkedInApp/i.test(navigator.userAgent);
};

const buildTelemetry = (context?: ApplyClickPortalContext) => {
  if (typeof window === "undefined") return undefined;
  const parser = new UAParser();
  const result = parser.getResult();
  const osLabel = [result.os.name, result.os.version].filter(Boolean).join(" ");
  const user =
    context?.userId || context?.userEmail
      ? { id: context.userId, email: context.userEmail }
      : undefined;

  return {
    device: {
      type: normalizeDeviceType(result.device.type),
      vendor: result.device.vendor || undefined,
      model: result.device.model || undefined,
      os: osLabel || undefined,
      screenResolution: window.screen
        ? `${window.screen.width}x${window.screen.height}`
        : "unknown",
    },
    browser: {
      name: result.browser.name || undefined,
      version: result.browser.version || undefined,
      engine: result.engine.name || undefined,
    },
    session: {
      id: getOrCreateSessionId(),
      referrer: document.referrer || "unknown",
      utmParams: parseUtmParams(window.location.search),
    },
    user,
    browserLanguage: navigator.language || undefined,
  };
};
export const getJobRedirectUrl = (baseUrl: string, params: { clientId?: string; clientName?: string; domainName?: string; source?: string; }) => {
  if (!baseUrl) return '';
  const urlParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value) {
      urlParams.append(key, value);
    }
  });

  const separator = baseUrl.includes('?') ? '&' : '?';

  return `${baseUrl}${separator}${urlParams.toString()}`;
}
export const useApplyClick = (context?: ApplyClickPortalContext) => {
  const trackAndRedirect = async (job: JobItemDto) => {
    if (!job.id && !job.ref_id) {
      return;
    }

    const linkedInInAppBrowser = isLinkedInInAppBrowser();

    // Pre-opening a blank tab breaks redirects in LinkedIn's in-app browser.
    const newTab =
      typeof window !== "undefined" && !linkedInInAppBrowser
        ? window.open("", "_blank")
        : null;

    const domainName =
      context?.domainName ||
      (typeof window !== "undefined" ? window.location.hostname : undefined);

    const requestParams = {
      clientId: context?.portalId,
      clientName:
        context?.clientName,
      domainName:
        context?.domainName,
      source: 'artha-link',
    };
    const payload = {
      job_id: job.id,
      ref_id: job.ref_id || undefined,
      redirect_url: isValidUrl(job.apply_url) ? job.apply_url : undefined,
      portal_id: context?.portalId,
      domain_name: domainName,
      client_name: context?.clientName,
      telemetry: buildTelemetry(context),
    };
    try {
      // const { ok, data } = await apiFetch<ApplyClickApiResponse>(
      //   "/jobs/apply-click",
      //   {
      //     method: "POST",
      //     body: JSON.stringify(payload),
      //   }
      // );
      // const redirectUrl = ok && data?.success ? data?.data?.redirect_url : undefined;
      const redirectUrl = getJobRedirectUrl(job?.url || "", requestParams);
      if (redirectUrl && isValidUrl(redirectUrl)) {
        if (newTab) {
          newTab.location.href = redirectUrl;
        } else if (linkedInInAppBrowser) {
          window.location.assign(redirectUrl);
        } else {
          const popup = window.open(redirectUrl, "_blank");
          if (!popup) {
            window.location.assign(redirectUrl);
          }
        }
      } else {
        newTab?.close();
      }
    } catch {
      newTab?.close();
    }
  };

  return { trackAndRedirect };
};

export type { ApplyClickPortalContext };
